import ReactGA from 'react-ga4';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import { DefaultThemeProvider } from 'theme/DefaultThemeProvider';
import { AuthProvider } from 'auth/AuthProvider';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorFallbackWithAuthorizationRedirect } from 'auth/ErrorFallbackWithAuthorizationRedirect';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider';
import { AppRoutes } from './AppRoutes';
import { ErrorCodes } from 'src/common/responseCodes';
import { getDateLocal } from 'i18n/dateLocale';
import { PermissionProvider } from 'permissions/PermissionProvider';
import { FeatureFlagProvider } from 'featureFlags/FeatureFlagProvider';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import './configuration/chartjs';
import 'overlayscrollbars/overlayscrollbars.css';
import { useHasFeatureFlag } from './featureFlags/useHasFeatureFlag';
import { Maintenance } from './common/pages/Maintenance';
import { useChartDefaultFontSize } from './common/components/Graphs/common/Graphs/hooks/useChartDefaultFontSize';
import '@fontsource-variable/inter/opsz.css';
import { MapProvider } from 'react-map-gl/mapbox';
import { AutoScalingWrapper } from 'src/common/components/AutoScalingWrapper';

if (import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID) {
  ReactGA.initialize(import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID);
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (error instanceof Error && error.message === ErrorCodes.AccessDenied) {
          return false;
        }

        return failureCount < 3;
      },
    },
  },
});

const MaintenanceWrapper = ({ children }: { children: React.ReactNode }) => {
  const maintenanceEnabled = useHasFeatureFlag('MaintenanceMode');
  if (maintenanceEnabled) {
    return <Maintenance />;
  }

  return children;
};

export const App = () => {
  useChartDefaultFontSize();
  return (
    <DefaultThemeProvider>
      <Box
        sx={{
          fontFamily: 'Inter Variable',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <ErrorBoundary FallbackComponent={ErrorFallbackWithAuthorizationRedirect}>
          <QueryClientProvider client={queryClient}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getDateLocal()}>
              <AuthProvider>
                <FeatureFlagProvider>
                  <AutoScalingWrapper>
                    <MaintenanceWrapper>
                      <PermissionProvider>
                        <MapProvider>
                          <CssBaseline />
                          <AppRoutes />
                        </MapProvider>
                      </PermissionProvider>
                    </MaintenanceWrapper>
                  </AutoScalingWrapper>
                </FeatureFlagProvider>
              </AuthProvider>
            </LocalizationProvider>
            <ReactQueryDevtools />
          </QueryClientProvider>
        </ErrorBoundary>
      </Box>
    </DefaultThemeProvider>
  );
};
